/* eslint-disable no-console */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import FilteredUnit from '../../constants/FilteredUnit';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import supplyChainAPI from '../../services/modules/IP/supplyChainAPI';
import cityJSON from '../../services/modules/JSON/cityJSON';
import frequencyJSON from '../../services/modules/JSON/frequency';
import modeOfTransportJSON from '../../services/modules/JSON/modeOfTransport';
import partnerTypeJSON from '../../services/modules/JSON/partnerType';
import statesJSON from '../../services/modules/JSON/statesJSON';
import vehicleType from '../../services/modules/JSON/vehicleType';
import supplierInfo from '../../services/modules/OM/supplierInfoDetails';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {shouldBlockFree} from '../../utils/SubscriptionHelper';
import DisplayModal from '../DisplayModal/DisplayModal';
import SupplyChainTableDisplay from '../DisplayModal/SupplyChainDisplay/SupplyChainTableDisplay';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  yearKeyLabel,
  facilityKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {
  upstreamKey,
  upstreamLabel
} from '../Forms/Constants/IP/UpstreamKeyLabel';
import UpstreamForm from '../Forms/UpstreamForm';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import sumTableValue from './Utils/sumTableValue';
import TableTitleHeader from './Utils/TableTitleHeader';

const UpstreamTable: React.FC<{
  selectedYear: string;
  facilityId: string;
}> = ({selectedYear, facilityId}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    name: string;
    mobile: string;
    email: string;
    partnerType: number;
    address: string;
    pincode: number;
    country: number;
    state: number;
    city: number;
    isEnvironmentalCriteria: boolean;
    isSustainableSourcing: boolean;
    frequencyOfTransportation: number;
    frequencyNumber: number;
    mode: number;
    classOftravel: number;
    quantity: number;
    quantityUnit: string;
    distanceTravelled: number;
    distanceTravelledUnit: string;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [suppliers, setSuppliers] = useState<ISelect[]>([]);
  const [displayTable, setDisplayTable] = useState(false);
  const [displayTableData, setDisplayTableData] = useState([]);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const {selectedCountry} = useContext(SelectedCountryContext);
  const [formFacility, setFormFacility] = useState<string>(facilityId);
  const [formSelectedYear, setFormSelectedYear] =
    useState<string>(selectedYear);
  const frequency = useJSONStore.getState().frequency;
  const mode = useJSONStore.getState().modeOfTransport;

  const type = 'upstream';

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear && allJSON) {
      setIsRefresh(true);
      Promise.all([
        supplyChainAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${facilityId}/${selectedYear}/${type}`
        ),
        supplierInfo(
          APITypeEnum.GET_ALL,
          undefined,
          `${facilityId}/${selectedYear}/${type}`
        )
      ])
        .then(([data, supplierInfo]) => {
          setDataSource(data ?? []);
          setSuppliers(
            supplierInfo.map((item: any) => {
              return {
                label: item?.[upstreamKey.SUPPLIER_NAME],
                value: item._id
              };
            })
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear, allJSON]);

  useEffect(() => {
    if (formFacility && formSelectedYear) {
      Promise.all([
        supplierInfo(
          APITypeEnum.GET_ALL,
          undefined,
          `${formFacility}/${formSelectedYear}/${type}`
        )
      ])
        .then(([supplierInfo]) => {
          setSuppliers(
            supplierInfo.map((item: any) => {
              return {
                label: item?.[upstreamKey.SUPPLIER_NAME],
                value: item._id
              };
            })
          );
        })
        .catch(() => {})
        .finally(() => {});
    }
  }, [formFacility, formSelectedYear]);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([
      vehicleType(),
      statesJSON(),
      cityJSON(),
      partnerTypeJSON(),
      frequencyJSON(),
      modeOfTransportJSON()
    ])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      supplyChainAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}/${type}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    supplyChainAPI(APITypeEnum.DELETE_ALL, {
      supplychain: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const upSupplychainKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key,
      isMaker: hasUserRole(UserRole.Maker_Role)
    },
    {
      key: upstreamKey.SUPPLIER,
      label: upstreamLabel.SUPPLIER,
      type: objTypes.json,
      format: suppliers
    },
    {
      key: upstreamKey.PRODUCT_DATA,
      label: 'Transportation Data',
      type: objTypes.supplyChainListData,
      format: [frequency, mode],
      renderFormat: [
        upstreamKey.FREQUENCY_TRANSPORT,
        upstreamKey.FREQUENCY_NUM,
        upstreamKey.MODE,
        upstreamKey.QUANTITY,
        upstreamKey.DISTANCE_TRAVELLED
      ],
      mainLabel: [
        upstreamLabel.FREQUENCY_TRANSPORT,
        upstreamLabel.FREQUENCY_NUM,
        upstreamLabel.MODE,
        upstreamLabel.QUANTITY,
        upstreamLabel.DISTANCE_TRAVELLED
      ],
      unit: [
        FilteredUnit(selectedCountry).weight,
        FilteredUnit(selectedCountry).distance
      ],
      renderType: 'multi'
    },
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return [
      {
        title: upstreamLabel.SUPPLIER,
        dataIndex: upstreamKey.SUPPLIER,
        ...SearchFilter(
          upstreamLabel.SUPPLIER,
          upstreamKey.SUPPLIER as keyof DataType,
          suppliers
        ),
        render: (value) => {
          return (
            <>
              {
                suppliers?.find((item) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: upstreamLabel.PRODUCT_DATA,
        dataIndex: upstreamKey.PRODUCT_DATA,
        render: (value) => {
          return (
            <div
              onClick={() => {
                if (shouldBlockFree()) {
                  usePlatformStore
                    .getState()
                    .setOpenOnFreeTrialMessageModel(true);

                  return;
                }
                setDisplayTable(true);
                setDisplayTableData(value);
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{value?.length}</div>
            </div>
          );
        }
      },
      {
        title: upstreamLabel.QUANTITY,
        dataIndex: upstreamKey.PRODUCT_DATA,
        render: (value) => {
          return `${sumTableValue(value, upstreamKey.QUANTITY)} ${
            value ? FilteredUnit(selectedCountry).weight : ''
          }`;
        }
      },
      {
        title: upstreamLabel.DISTANCE_TRAVELLED,
        dataIndex: upstreamKey.PRODUCT_DATA,
        render: (value) => {
          return `${sumTableValue(value, upstreamKey.DISTANCE_TRAVELLED)} ${
            value ? FilteredUnit(selectedCountry).distance : ''
          }`;
        }
      },
      // {
      //   title: upstreamLabel.PARTNER_TYPE,
      //   dataIndex: upstreamKey.PARTNER_TYPE,

      //   type: 'select',
      //   ...sorter(upstreamKey.PARTNER_TYPE as keyof DataType),
      //   //   width: '100px',
      //   filterer: partnerType,
      //   filters: partnerType?.map((item) => {
      //     return {text: item.label, value: item.value};
      //   }),
      //   onFilter: (value, record) => {
      //     return record[upstreamKey.PARTNER_TYPE as keyof DataType] === value;
      //   },
      //   render: (value) => {
      //     return (
      //       <>
      //         {
      //           partnerType?.find((item) => {
      //             return item.value === value;
      //           })?.label
      //         }
      //       </>
      //     );
      //   }
      // },
      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: makeCheckKeyLabel.STATUS_COMMENTS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: makeCheckKeyLabel.STATUS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      if (shouldBlockFree()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      if (shouldBlockFree()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px !important',
              render: (value: any, record: any) =>
                dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <>
      <div>
        <TableTitleHeader
          title="Upstream Info"
          filter={{facilityId, selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Upstream'
          }}
          sampleDownload={{href: SampleExcel.SupplyUpstream}}
          jobs={{setShowJobs, uploadType: uploadTypes.SupplyUpstream}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          showAddOnly={true}
          shouldEdit={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          }
        />
        <JobsModal
          open={showJobs}
          onCancel={() => {
            setShowJobs(false);
            setRefresh(true);
          }}
          type={uploadTypes.SupplyUpstream}
          details={`${facilityId}/${selectedYear}`}
        />

        <TableAnt
          // getPopupContainer={(triggerNode) =>
          //   triggerNode.closest(
          //     '.ant-modal, .ant-drawer, [style*="position"]'
          //   ) as HTMLElement
          // }
          rowSelection={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
              ? {
                  type: 'checkbox',
                  getCheckboxProps: (record: any) => ({
                    disabled: record.status === statusEm.ACCEPTED
                  }),
                  selectedRowKeys,
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item) => {
                        return item._id;
                      })
                    );
                  }
                }
              : undefined
          }
          // expandable={{expandedRowRender:<ExpandedRowRender/>,defaultExpandedRowKeys:}}

          rowKey="_id"
          dataSource={dataSource}
          columns={columns as any}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: allJSON ? isRefresh : true
          }}
        />
        <DrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          setRefresh={setRefresh}
          data={isEdit ? editData : undefined}
          FormComponent={
            <UpstreamForm
              setSubmitDisabled={setSubmitDisabled}
              suppliers={suppliers}
              setFormFacility={setFormFacility}
              setFormSelectedYear={setFormSelectedYear}
            />
          }
          API={supplyChainAPI}
          idKey={`${facilityId}/${selectedYear}`}
          table={true}
          type={{key: 'type', value: type}}
          buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
          stepsTitle={['Configure Details', 'Supply Chain Details']}
          formPreview={upSupplychainKeyLabel}
        />
        <DisplayModal
          title={upstreamLabel.PRODUCT_DATA}
          open={displayTable}
          onCancel={() => {
            setDisplayTable(false);
          }}
          displayComponent={
            <SupplyChainTableDisplay dataSource={displayTableData} />
          }
          isTable={true}
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        // title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo
            formData={editData}
            formPreview={upSupplychainKeyLabel}
          />
        }
        API={supplyChainAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        // formPreview={upSupplychainKeyLabel}
      />
    </>
  );
};

export default UpstreamTable;
